import { z } from 'zod';
import { EOrganization, ERef } from '.';

export type LayoutHeader = {
  headerId: string;
  width: 'column' | 'layout';
};

export type AllowedLayout = {
  columns: number;
  heightPixels: number;
  layoutHeader: LayoutHeader | null;
  id: string;
  fillers: Filler[];
};

const FillerSchema = z.object({
  name: z.string(),
  pdfUrl: z.string(),
  columns: z.number(),
  height: z.number(),
  id: z.string(),
  maxOccurrences: z.number()
});
export type Filler = z.infer<typeof FillerSchema>;

const SeparatorConfigSchema = z.object({
  enabled: z.boolean(),
  includeColumns: z.array(z.number())
});
export type SeparatorConfig = z.infer<typeof SeparatorConfigSchema>;
export const DEFAULT_SEPARATOR_CONFIG: SeparatorConfig = {
  enabled: false,
  includeColumns: []
};

const VariantConfigSchema = z.object({
  disabled: z.boolean()
});

const BorderingConfigSchema = z.object({
  thickness: z.number(),
  excludedColumns: z.array(z.number())
});
export type BorderingConfig = z.infer<typeof BorderingConfigSchema>;

export const OptimizerHyperparamsSchema = z.object({
  max_time_seconds: z.number(),
  max_whitespace_percentage: z.number(),
  max_filler_percentage: z.number(),
  enable_split_points: z.boolean(),
  enable_page_splitting: z.boolean(),
  min_element_margin: z.number()
});
export type OptimizerHyperparams = z.infer<typeof OptimizerHyperparamsSchema>;

const LayoutSettingsConfigSchema = z.object({
  minHeight: z.number(),
  heightIncrements: z.number(),
  headerId: z.string().optional(),
  minColumns: z.number().optional()
});
export type LayoutSettingsConfig = z.infer<typeof LayoutSettingsConfigSchema>;
export const DEFAULT_LAYOUT_SETTINGS: LayoutSettingsConfig = {
  minHeight: 72,
  heightIncrements: 36,
  // This is checked in layoutIsValidForOrders, ensuring that all layouts we present to our
  // optimizer have at least this number of columns
  // This can be used as a way of enforcing "horizontal prioritization", by forcing all layouts
  // to take up the full width
  // Should this strategy be insufficient, we should follow up by improving getPotentialPublisherLayouts
  // with an explicit "horizontal" planning mode
  minColumns: 1
};

const HeaderSchema = z.object({
  applyToColumns: z.array(z.number()),
  url: z.string(),
  width: z.number(),
  id: z.string()
});
type Header = z.infer<typeof HeaderSchema>;
type HeaderWithHeight = Header & {
  height: number;
};
const HeaderSettingsConfigSchema = z.object({
  headers: z.array(HeaderSchema)
});
export type HeaderSettingsConfig = z.infer<typeof HeaderSettingsConfigSchema>;
export type HeaderSettingsConfigWithHeight = {
  headers: HeaderWithHeight[];
};

const ExternalAdsConfigSchema = z.object({
  bucket: z.string(),
  momentFormattedFolder: z.string()
});
export type ExternalAdsConfig = z.infer<typeof ExternalAdsConfigSchema>;
export const DEFAULT_EXTERNAL_ADS_CONFIG: ExternalAdsConfig = {
  bucket: 'nypa-document-migration',
  momentFormattedFolder: 'YYYY/MM-DD'
};

const CombinedPublisherConfigSchema = z.object({
  includedPublisherIds: z.array(z.string()),
  enabled: z.boolean()
});
export type CombinedPublisherConfig = z.infer<
  typeof CombinedPublisherConfigSchema
>;
export const DEFAULT_COMBINED_PUBLISHERS_CONFIG: CombinedPublisherConfig = {
  includedPublisherIds: [],
  enabled: false
};

/**
 * The settings Column admins edit from the auto pagination tab. Ownership fields
 * are deliberately excluded so an update can't reassign the settings document.
 */
export const EditablePublisherPaginationSettingsSchema = z.object({
  fillers: z.array(FillerSchema.omit({ height: true })),
  separators: SeparatorConfigSchema.optional(),
  variants: VariantConfigSchema.optional(),
  bordering: BorderingConfigSchema.nullish(),
  optimizerHyperparams: OptimizerHyperparamsSchema.nullish(),
  layoutSettings: LayoutSettingsConfigSchema.nullish(),
  headerSettings: HeaderSettingsConfigSchema.nullish(),
  externalAdSettings: ExternalAdsConfigSchema.nullish(),
  combinedPublisherConfig: CombinedPublisherConfigSchema.nullish()
});

export type PublisherPaginationSettings = z.infer<
  typeof EditablePublisherPaginationSettingsSchema
> & {
  enableAdSplitting: boolean;
  flowText: boolean;
  allowedPublishers: ERef<EOrganization>[];
  owner: ERef<EOrganization>;
};
